<template>
  <ul class="type-specimen">
    <li v-for="face in faces" :key="face" class="type-face">
      <span class="type-face-name">{{ face }}</span>

      <!-- The sample is set in the face itself, which is the whole point of a
           specimen: a name says which typeface a build was set in, and only the
           letterforms say what that means. It is the one thing in the spec sheet
           that cannot be a chip.

           A pangram rather than a phrase, because the sentence has to carry every
           letter to be worth looking at, and it is not translated: the faces are
           Latin, so a specimen in Han characters or in Jawi would be a picture of
           the fallback face rather than of the one being shown. The name above the
           sentence is the text a reader needs; the sentence is the demonstration
           beside it.

           The sentence does not wrap. A specimen that wrapped would break the one
           thing it exists to show, the shape of a line at a single size, so it
           runs past the column and the row scrolls instead. A scroll region is
           reachable by keyboard, so the row is focusable and names itself. -->
      <div
        class="type-face-scroll"
        role="region"
        :aria-label="face"
        tabindex="0"
      >
        <span class="type-face-sample" :style="family(face)">
          The quick brown fox jumps over the lazy dog.
        </span>
      </div>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* A face, shown by setting a line in it.

   It came out of the case study's spec sheet, where the typeface row was a pair
   of chips reading "Rubik" and "Inter", which named the faces without showing
   anything about them. A specimen is its own object, so it is its own component
   rather than a class on the page, and it will be the same object the next time
   a page has to show which face something is set in.

   The name is a code, not a label: it is the value the build states, the way a
   swatch states a hex, so it takes the mono step a value takes and stays in the
   case it is spelled in, because a typeface's name is a proper noun rather than
   a label to be uppercased.

   The name is read at the body ink rather than the muted one, and a step larger
   than it was drawn. A specimen is being read for its name: that name is what
   says which face the sentence below is set in, and the sentence is the
   demonstration beside it. At 0.7rem in $text-muted it was small text at about
   2.7:1 against the tinted ground the spec plate stands on, which is under the
   4.5:1 that size of text needs, so a reader who did not already know the two
   names had the quieter half of the pair. $text-body is about 5.3:1 on the same
   ground and is the ink the page's own reading copy takes, and $text-muted stays
   what it is for: the step a caption takes, not the name a build is identified
   by. The sample keeps its place, because the face is still set at nearly twice
   the size and in the heading ink, and 0.85rem holds the name under the group's
   own index rather than turning the pair into two competing lines.

   Nothing here responds to the pointer. A specimen is read, not chosen. */
.type-specimen {
  display: grid;
  gap: 1.15rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.type-face {
  display: grid;
  gap: 0.3rem;
  min-width: 0;
}

.type-face-name {
  color: $text-body;
  font-family: $font-mono;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  line-height: 1.3;
}

/* The row scrolls rather than wrapping or truncating, which is the site's one
   answer to content wider than its column: `.table-scroll` does the same and
   leaves the bar where the platform draws it, so the bar is also the affordance
   that says the line continues. `overscroll-behavior-x` stops a swipe that
   reaches the end of the specimen from carrying on into the page and, on a
   browser that reads a horizontal swipe as navigation, from leaving it.

   The block padding is not decoration. `overflow-x: auto` forces the other axis
   to compute to `auto` as well, so this row is a scroll box in both directions
   and clips the line to the box the line-height gives it. Rubik and Inter
   overshoot a 1.25 line box by about a pixel at each end, so the descenders of
   the pangram were being shaved against the clip edge and the line read as
   cramped inside a border that was not there. The padding is what puts the
   overshoot back inside the clip, and it is on the scroll box rather than on the
   sentence because the box is what clips. It is block only: an inline inset would
   sit at the start of the scrolled line and push the specimen off its column.

   The half rem is a step more than the overshoot alone needs, and it is there for
   the bar rather than for the type. A row that scrolls carries the platform's own
   scrollbar inside this box, and the bar is itself a strip of the box's height, so
   padding that only cleared the descenders left the line sitting directly on top
   of it. Half a rem on either side is what separates the two, and the overshoot
   is then covered twice over, which is the margin that keeps a fractional
   line-height at a fractional width from shaving an edge again.

   The focus ring is here because _reset.scss strips focus globally, so a
   focusable element has to put its own back, and this one is focusable only
   because a scroll region a keyboard cannot reach hides the rest of its line. */
.type-face-scroll {
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding: 0.8rem 0;

  &:focus-visible {
    border-radius: $r-xs;
    outline: 3px solid $ring-focus;
    outline-offset: 2px;
  }
}

/* The size is the specimen's, not the page's: a face has to be set large enough
   for the letterforms to be compared, and 1.6rem is the smallest that still
   shows the difference between a humanist and a geometric face in a line of
   running text rather than only in one glyph. The weight is the one the site sets
   its own headings in, so Rubik is shown at a weight the reader has already seen
   it in elsewhere on the site. Only a length in the `font-family` can come from
   the data, so it arrives on the element's style. */
.type-face-sample {
  color: $text-heading;
  display: block;
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
}
</style>

<script>
export default {
  props: {
    /* The faces as they are named in the catalog, e.g. ["Rubik", "Inter"].
       Brand names, so they are never translated. */
    faces: {
      type: Array,
      required: true
    }
  },

  methods: {
    /* A face name as a font stack. The names come from the catalog, so this is
       the one style on the component that is data rather than a decision, and it
       is quoted because a face name may carry a space.

       `sans-serif` is the fallback because that is what the family being shown
       is: the specimen is a sans in every project so far, and the fallback is
       only reached if a family has not been loaded, where any readable line beats
       an invisible one. */
    family(face) {
      return { fontFamily: `"${face}", sans-serif` };
    }
  }
};
</script>
